import React, { memo } from 'react';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { SidebarBoundary } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/sidebar-boundary/index.tsx';
import type { JsmProjectMenuProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-projects-jsm-project-menu/src/ui/project-menu/JsmProjectMenu.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { SkeletonNavMenuItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { usePathParam } from '@atlassian/react-resource-router';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition';
import { fg } from '@atlassian/jira-feature-gating';

const Skeleton = () => <SkeletonNavMenuItems count={1} />;

const LazySidebarContent = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-jsm-project-menu" */ './ui/project-menu'
		).then(({ JsmProjectMenu }) => JsmProjectMenu),
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: true,
	},
);
const LazySidebarContentWrapperWithProjectKeyCheck = (props: JsmProjectMenuProps) => {
	const [projectKey] = usePathParam('projectKey');
	if (fg('support-getting-customization-using-issue-key')) {
		return <LazySidebarContentWrapper {...props} />;
	}

	if (fg('more-remounting-fix')) {
		return <LazySidebarContent {...props} />;
	}

	if (!projectKey) {
		return <LazySidebarContent {...props} />;
	}

	if (typeof __SERVER__ !== 'undefined' && __SERVER__) {
		return <LazySidebarContentWrapper {...props} />;
	}
	return <LazySidebarContent {...props} />;
};

const LazySidebarContentWrapper = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-jsm-project-menu-wrapper" */ './ui/project-menu/JsmProjectMenuWrapper'
		).then(({ JsmProjectMenuWrapper }) => JsmProjectMenuWrapper),
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: true,
	},
);

const JsmProjectMenuLazy = componentWithCondition(
	() => fg('more_for_nav_4_level_3'),
	LazySidebarContentWrapperWithProjectKeyCheck,
	LazySidebarContent,
);

export const AsyncJsmProjectMenu = memo(
	({ projectData, spotlight, menuItemButtonRef }: JsmProjectMenuProps) => (
		<SidebarBoundary
			id="nav4.sidebar.jsm-project-menu"
			packageName="jiraNavigationAppsSidebarNav4JsmProjectMenu"
			teamName="jsm-getting-started"
			ufoName="nav4.sidebar.jsm-project-menu"
		>
			<Placeholder name="nav4.sidebar.jsm-project-menu.loading-lazy" fallback={<Skeleton />}>
				<JsmProjectMenuLazy
					projectData={projectData}
					spotlight={spotlight}
					menuItemButtonRef={menuItemButtonRef}
				/>
			</Placeholder>
		</SidebarBoundary>
	),
);
